import React, { useState, useEffect } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { FaEnvelope, FaPhone, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import useForceLightTheme from "../../hooks/useForceLightTheme";
import { useBranding } from "../../contexts/BrandingContext";
import InstitutePicker from "./InstitutePicker";
import vcLogo from "../../Assets/Images/Logos/logo.png";
import vcFavIcon from "../../Assets/Images/Logos/favicon.png";
import lmsLogo from "../../Assets/Images/Logos/lms_logo.png";
import lmsFavIcon from "../../Assets/Images/Logos/favicon.png";
import studentImg from "../../Assets/Images/login_bg.png";
import "../Auth/Login.scss";

const isLmsCampus = () => {
  const h = window.location.hostname;
  return h.endsWith('lmscampus.com') || h === 'localhost';
};
const logo = isLmsCampus() ? lmsLogo : vcLogo;
const favIcon = isLmsCampus() ? lmsFavIcon : vcFavIcon;

const ROLES = [
  { value: "student", label: "Student", icon: "🎓" },
  { value: "instructor", label: "Instructor", icon: "👨‍🏫" },
  { value: "admin", label: "Admin", icon: "⚙️" },
];

const Signup = () => {
  useForceLightTheme();
  const branding = useBranding();
  const brandLogo = branding.logoUrl || logo;
  const brandFavIcon = branding.faviconUrl || branding.logoUrl || favIcon;
  const brandName = branding.brandName;
  const supportEmail = branding.supportEmail;

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }
  const query = useQuery();

  const [form, setForm] = useState({
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    role: "student",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [institutes, setInstitutes] = useState(null); // multi-institute picker

  useEffect(() => {
    const email = query.get("email");
    const mobile = query.get("mobile");
    const role = query.get("role");
    setForm((prev) => ({
      ...prev,
      email: email || prev.email,
      mobile: mobile || prev.mobile,
      role: role || prev.role,
    }));
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setMsg({ type: "", text: "" });
  };

  // Core signup call — extracted so institute picker can re-invoke with chosen instituteId
  const doSignup = async (extraPayload = {}) => {
    setLoading(true);
    setMsg({ type: "", text: "" });
    try {
      const payload = {
        email: form.email,
        mobile: form.mobile,
        password: form.password,
        role: form.role,
        ...extraPayload,
      };
      if (form.role === "student") {
        await apiClient.post("/auth/student-signup", payload);
        setMsg({ type: "success", text: "Registration successful! Please login." });
        setForm({ email: "", mobile: "", password: "", confirmPassword: "", role: "student" });
        setAcceptedTerms(false);
      } else if (form.role === "instructor") {
        await apiClient.post("/auth/instructor-signup", payload);
        setMsg({ type: "success", text: "Registration successful as instructor! Please login." });
      } else if (form.role === "admin") {
        await apiClient.post("/auth/admin-signup", payload);
        setMsg({ type: "success", text: "Registration successful as admin! Please login." });
      }
      setInstitutes(null);
    } catch (err) {
      const resp = err?.response;
      // HTTP 300 — multi-institute picker
      if (resp?.status === 300 && resp?.data?.multiInstitute) {
        setInstitutes(resp.data.institutes);
      } else {
        setMsg({ type: "danger", text: resp?.data?.message || "Signup failed!" });
      }
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (!acceptedTerms) {
      setMsg({ type: "danger", text: "Please accept the Terms & Conditions to continue." });
      return;
    }

    if (form.password !== form.confirmPassword) {
      setMsg({ type: "danger", text: "Passwords do not match." });
      return;
    }

    await doSignup();
  };

  // User picked an institute from the picker
  const handleInstitutePick = (inst) => {
    setInstitutes(null);
    doSignup({ instituteId: inst.instituteId });
  };

  return (
    <div className="auth-page">
      {/* Mobile top navbar */}
      <div className="auth-mobile-navbar">
        <Link to="/" className="auth-mobile-navbar-logo">
          <img src={brandFavIcon} alt="Logo" />
          <span>{brandName}</span>
        </Link>
        <Link to="/login" className="auth-mobile-navbar-btn">
          Sign In
        </Link>
      </div>

      <div className="auth-container">
        {/* Left: Branding Panel */}
        <div className="auth-brand-panel">
          <div className="auth-brand-content">
            <div className="auth-brand-badge">
              <img src={brandFavIcon} alt="Logo" className="auth-brand-icon" />
            </div>
            <h1 className="auth-brand-title">{brandName}</h1>
            <p className="auth-brand-subtitle">Join thousands of learners and educators on our platform</p>
            <div className="auth-brand-illustration">
              <img src={studentImg} alt="Learning illustration" />
            </div>
            <div className="auth-brand-features">
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Free to get started</span>
              </div>
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>AI-powered practice sessions</span>
              </div>
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Assignments & assessments</span>
              </div>
            </div>
          </div>
          <div className="auth-brand-footer">
            <img src={brandLogo} alt={brandName} className="auth-brand-logo" />
          </div>
        </div>

        {/* Right: Form Panel */}
        <div className="auth-form-panel">
          <div className="auth-form-wrapper">
            <div className="auth-form-header">
              <h2>Create your account</h2>
              <p>Start your learning journey today</p>
            </div>

            {/* Role Selector */}
            <div className="auth-role-selector">
              <label className="auth-role-label">I want to join as</label>
              <div className="auth-role-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                {ROLES.map((role) => (
                  <button
                    key={role.value}
                    type="button"
                    className={`auth-role-chip ${form.role === role.value ? "active" : ""}`}
                    onClick={() => setForm((prev) => ({ ...prev, role: role.value }))}
                  >
                    <span className="auth-role-icon">{role.icon}</span>
                    <span>{role.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <Form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-input-group">
                <label>Email Address</label>
                <div className="auth-input-wrapper">
                  <FaEnvelope className="auth-input-icon" />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="auth-input-group">
                <label>Mobile Number</label>
                <div className="auth-input-wrapper">
                  <FaPhone className="auth-input-icon" />
                  <input
                    type="tel"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    placeholder="Enter your mobile number"
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="auth-input-group">
                <label>Password</label>
                <div className="auth-input-wrapper">
                  <FaLock className="auth-input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    minLength={5}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="auth-input-group">
                <label>Confirm Password</label>
                <div
                  className="auth-input-wrapper"
                  style={
                    form.confirmPassword && form.password !== form.confirmPassword
                      ? { borderColor: "#ef4444" }
                      : {}
                  }
                >
                  <FaLock className="auth-input-icon" />
                  <input
                    type={showConfirm ? "text" : "password"}
                    placeholder="Re-enter your password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={5}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowConfirm(!showConfirm)}
                    tabIndex={-1}
                  >
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <span style={{ fontSize: "0.8rem", color: "#ef4444", marginTop: 2 }}>
                    Passwords do not match
                  </span>
                )}
              </div>

              {/* Terms & Conditions */}
              <div className="auth-terms-group">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
                <label htmlFor="acceptTerms">
                  I agree to the{" "}
                  <Link to="/terms-and-conditions" target="_blank">
                    Terms & Conditions
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms-and-conditions" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              {msg.text && (
                <Alert variant={msg.type} className="auth-alert">
                  {msg.text}
                </Alert>
              )}

              <Button
                type="submit"
                className="auth-submit-btn"
                disabled={loading || !acceptedTerms}
              >
                {loading ? <Spinner size="sm" animation="border" /> : "Create Account"}
              </Button>
            </Form>

            <div className="auth-footer-text">
              Already have an account?{" "}
              <Link to="/login" className="auth-action-link">
                Sign in
              </Link>
            </div>

            {supportEmail && (
              <div className="auth-footer-text" style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
                Need help?{" "}
                <a href={`mailto:${supportEmail}`} className="auth-action-link">
                  {supportEmail}
                </a>
              </div>
            )}

            <div className="auth-footer-logo">
              <img src={brandLogo} alt={brandName} />
            </div>
          </div>
        </div>
      </div>

      {/* Multi-Institute Picker */}
      {institutes && (
        <InstitutePicker
          institutes={institutes}
          onSelect={handleInstitutePick}
          onCancel={() => setInstitutes(null)}
          loading={loading}
          title="Choose Your Institute"
        />
      )}
    </div>
  );
};

export default Signup;
